/**
 * 运势预测API
 * 后端服务端口：5042
 */

// 基础URL配置
const getBaseUrl = () => {
  const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isHttps = window.location.protocol === 'https:';
  
  if (isDevelopment) {
    return 'http://localhost:5042';
  } else {
    // 生产环境配置
    if (isHttps) {
      return 'https://aiinovavida.net';
    } else {
      return 'http://aiinovavida.net';
    }
  }
};

/**
 * 调用运势预测API
 * @param {string} name - 姓名
 * @param {string} gender - 性别：male/female
 * @param {string} birthDate - 出生日期（格式化后的字符串）
 * @param {Object} birthPlace - 出生地信息
 * @param {string} language - 语言：zh/en/es
 * @returns {Promise} 预测结果
 */
export const callFortuneAPI = async (name, gender, birthDate, birthPlace, language = 'zh', birthPlace2 = null) => {
  try {
    // 构建可用的API地址列表（按优先级排序）
    const apiCandidates = [];
    
    // 1. 相对路径（适用于生产环境Nginx代理和开发环境Vite代理）
    // apiCandidates.push('/api/fortune/predict');
    
    // 2. 开发环境本地API
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      apiCandidates.push('http://localhost:5042/api/fortune/predict');
      apiCandidates.push('http://127.0.0.1:5042/api/fortune/predict');
    }
    
    // 3. 生产环境API地址
    if (window.location.protocol === 'https:') {
      apiCandidates.push('https://aiinovavida.net/api/fortune/predict');
      apiCandidates.push('https://www.aiinovavida.net/api/fortune/predict');
    } else {
      apiCandidates.push('http://aiinovavida.net/api/fortune/predict');
      apiCandidates.push('http://www.aiinovavida.net/api/fortune/predict');
    }
    
    console.log('API调用候选地址:', {
      isDevelopment: window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
      isHttps: window.location.protocol === 'https:',
      hostname: window.location.hostname,
      candidates: apiCandidates,
      name, gender, birthDate, birthPlace, language
    });
    
    let lastError = null;
    
    // 按顺序尝试所有候选地址
    for (let i = 0; i < apiCandidates.length; i++) {
      const apiUrl = apiCandidates[i];
      console.log(`尝试API地址 [${i + 1}/${apiCandidates.length}]: ${apiUrl}`);
      
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name,
            gender,
            birthDate,
            birthPlaceProvince: birthPlace?.provinceName || birthPlace?.province || '',
            birthPlaceCity: birthPlace?.cityName || birthPlace?.city || '',
            birthPlaceCountry2: birthPlace2?.country || '',
            birthPlaceCity2: birthPlace2?.region || '',
            predictionDate: new Date().toISOString().split('T')[0].replace(/-/g, ''),
            language
          })
        });
        
        console.log(`API响应状态: ${response.status} ${response.statusText}`);
        
        if (response.ok) {
          const data = await response.json();
          console.log(`API调用成功，使用地址: ${apiUrl}`);
          return data;
        } else {
          // 如果不是404，可能是其他错误，继续尝试下一个
          const errorText = await response.text();
          lastError = new Error(`API请求失败 (${response.status}): ${errorText.substring(0, 100)}`);
          console.warn(`API地址 ${apiUrl} 失败:`, lastError.message);
          
          // 如果是404，继续尝试下一个
          if (response.status !== 404) {
            // 对于非404错误，可以提前停止尝试
            break;
          }
        }
      } catch (error) {
        lastError = error;
        console.warn(`API地址 ${apiUrl} 网络错误:`, error.message);
        // 继续尝试下一个地址
      }
    }
    
    // 所有地址都尝试失败
    if (lastError) {
      throw lastError;
    } else {
      throw new Error('所有API地址尝试失败');
    }
    
  } catch (error) {
    console.error('API调用错误详情:', {
      message: error.message,
      stack: error.stack,
      name: error.name,
      timestamp: new Date().toISOString()
    });
    
    // 提供更友好的错误信息
    let userMessage = error.message;
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      userMessage = '网络连接失败，请检查：1) API服务器是否运行 2) 网络设置 3) 防火墙设置';
    } else if (error.message.includes('NetworkError')) {
      userMessage = '网络错误，请确保您已连接到互联网';
    } else if (error.message.includes('CORS')) {
      userMessage = '跨域请求被阻止，请检查API服务器CORS配置';
    } else if (error.message.includes('404')) {
      userMessage = 'API接口未找到，请检查服务器配置';
    } else if (error.message.includes('混合内容')) {
      userMessage = '安全连接问题：HTTPS页面不能访问HTTP接口';
    }
    
    // 添加开发环境特定提示
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      userMessage += ' (开发环境提示：请确保API服务器运行在localhost:5042)';
    }
    
    // 添加微信浏览器特定提示
    const isWeChat = /MicroMessenger/i.test(navigator.userAgent);
    if (isWeChat && error.message.includes('Failed to fetch')) {
      userMessage = '微信浏览器网络限制，请尝试：1) 切换到系统浏览器 2) 检查网络代理设置';
    }
    
    const enhancedError = new Error(userMessage);
    enhancedError.originalError = error;
    enhancedError.isNetworkError = error.name === 'TypeError';
    throw enhancedError;
  }
};

/**
 * 简化版运势预测API（使用基础URL）
 */
export const predictFortune = async (data) => {
  try {
    const baseUrl = getBaseUrl();
    const apiUrl = `${baseUrl}/api/fortune/predict`;
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...data,
        predictionDate: new Date().toISOString().split('T')[0].replace(/-/g, '')
      })
    });
    
    if (response.ok) {
      return await response.json();
    } else {
      const errorText = await response.text();
      throw new Error(`预测失败 (${response.status}): ${errorText}`);
    }
  } catch (error) {
    console.error('预测API调用失败:', error);
    throw error;
  }
};